"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-16 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 font-default text-base font-medium text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col p-4 gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <div className="flex flex-col py-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block px-6 py-4 font-default text-lg font-medium text-foreground hover:bg-accent hover:text-primary" onClick={onClose}>
            {item.label}
          </Link>
        </div>)}
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="222538" id="header" className={`sticky top-0 z-50 w-full transition-all duration-400 bg-background text-foreground ${isScrolled ? "border-b border-border bg-background/80 shadow-xl backdrop-blur-md" : "border-b border-border bg-background"}`}>
      <header className="container mx-auto flex h-20 items-center justify-between px-4 lg:px-8">
        {}
        <Link to="/HomePage" className="flex shrink-0 items-center">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654438/img/sexpleasure_editorial_logo.png" alt="sexpleasure.store" className="h-8 w-auto" />
        </Link>

        {}
        <div className="hidden items-center gap-8 md:flex">
          <MenuProvider menu_id="primary">
            <DesktopMenuConsumer />
          </MenuProvider>
          
          <Button variant="default" className="h-auto rounded-full bg-primary px-6 py-3 font-default text-sm font-semibold tracking-tight text-primary-foreground transition-transform hover:scale-105 hover:bg-primary/90">
            Join the Club
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="flex md:hidden text-foreground" aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </header>

      {}
      {isMobileMenuOpen && <div className="absolute left-0 top-full w-full border-b border-border bg-background shadow-2xl md:hidden">
          <MenuProvider menu_id="primary">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="border-t border-border p-6">
            <Button variant="default" className="h-auto w-full rounded-full bg-primary py-4 font-default text-sm font-semibold tracking-tight text-primary-foreground transition-transform hover:scale-105 hover:bg-primary/90" onClick={() => setIsMobileMenuOpen(false)}>
              Join the Club
            </Button>
          </div>
        </div>}
    </section>;
}